"use client";

import { useState, useMemo, useEffect, useContext } from "react";
import TableOfContent from "../TableOfContent";
import "./index.css";
import { useNavigate } from "react-router-dom";
import {
  CarouselProvider,
  Slider,
  Slide,
  CarouselContext,
} from "pure-react-carousel";
import "pure-react-carousel/dist/react-carousel.es.css";
import Cookies from "js-cookie";

export default function ContentComponent(props) {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(true);
  const [popup, setPopup] = useState(false);
  const [currentRef, setRef] = useState(0);
  const [activeItem, setActiveItem] = useState(null);
  const [tokenExpiry, setExpiry] = useState(false);
  const token = Cookies.get("sas_token");

  const handleItemClick = (section) => {
    const indexMap = {
      // AI Risk Scores & Progression Models
      AICVD: 0,
      Prediabetes: 1,
      COPD: 2,
      Liver: 3,
      CKD: 4,
      hypertension: 5,

      // AI Augmented Pathways
      EARS: 0,
      EWS: 1,
      Anesthesia: 2,

      // Images x Signals
      CXR: 0,
      TB: 1,
      mirai: 2,
      sybil: 3,
      ecg: 4,

      // Generative AI
      ncp: 0,
      "radiology": 1,
      "vtb": 2,
      "GenAI": 3,

      // NLP Techniques
      "PII": 0,
      "CER": 1,
    };

    const index = indexMap[section];
    if (index !== undefined) {
      setRef(index);
      setIsPlaying(false);
      setActiveItem(section);
      setTimeout(() => {
        setIsPlaying(true);
      }, 2000);
    } else {
      setActiveItem(section);
    }
  };

  const noAccessPopup = () => {
    setPopup(true);
  };

  const closetoggle = () => {
    setPopup(false);
  };

  const getImageBlobUrl = useMemo(() => {
    const imageMap = new Map(props.images.map((img) => [img.id, img.blobUrl]));
    return (id) => imageMap.get(id) || "";
  }, [props.images]);

  function isTokenExpired(url) {
    try {
      const urlParams = new URL(url).searchParams;
      const expiryTime = urlParams.get("se");

      if (expiryTime) {
        const expiryDate = new Date(expiryTime);
        const now = new Date();
        return now > expiryDate;
      }

      setExpiry(true);
      return true;
    } catch (error) {
      console.error("Error parsing URL:", error);
      setExpiry(true);
      return true;
    }
  }

  const isMobile = window.innerWidth <= 768;

  function SlideTracker() {
    const carouselContext = useContext(CarouselContext);

    useEffect(() => {
      function onChange() {
        const { currentSlide } = carouselContext.state;
        setRef(currentSlide);
        if (props.contentList && props.contentList.length > 0) {
          const currentContent = props.contentList[currentSlide];
          if (currentContent) {
            setActiveItem(currentContent.indexName);
          }
        }
      }
      carouselContext.subscribe(onChange);

      onChange();

      return () => carouselContext.unsubscribe(onChange);
    }, [carouselContext]);

    return null;
  }

  const handleGetStarted = (indexName) => {
    const appName = indexName.toLowerCase().trim();
    const normalizedApps =
      props.availableApps?.map((app) => app.trim().toLowerCase()) || [];
    if (!token) {
      navigate("/login");
      return;
    }

    if (!props.availableApps || props.availableApps.length === 0) {
      return;
    }

    if (normalizedApps.includes(appName)) {
      navigate(`/${appName}`);
    } else {
      noAccessPopup?.();
    }
  };

  return (
    <div id="" className="front-page-container">
      <div
        className="list-of-scrolling-items"
        onMouseOver={() => setIsPlaying(false)}
        onMouseLeave={() => setIsPlaying(true)}
      >
        <CarouselProvider
          naturalSlideWidth={100}
          naturalSlideHeight={120}
          totalSlides={props.contentList.length}
          isPlaying={isPlaying}
          interval={5000}
          infinite={true}
          style={{ height: isMobile ? "100vh" : "100%" }}
          className="carousel-provider"
          currentSlide={currentRef}
        >
          <SlideTracker />

          <Slider className="slider-block-container">
            {props.contentList.map((content, i) => {
              const imageUrl =
                isTokenExpired(
                  `https://clinicalpubres.blob.core.windows.net/pubres/clinical-img/${content.name}-home-page.jpg?${token}`
                ) || tokenExpiry
                  ? getImageBlobUrl(
                      `home-image-${content.indexName.toLowerCase()}`
                    )
                  : `https://clinicalpubres.blob.core.windows.net/pubres/clinical-img/${content.name}-home-page.jpg?${token}`;

              return (
                <Slide
                  index={i}
                  key={content.indexName}
                  value={content.indexName}
                  style={{ height: "100%" }}
                >
                  <div className="contentBox" id={content.title + i}>
                    <div className="leftSection">
                      <div
                        className="left-section-content"
                        style={{
                          padding: "56px",
                        }}
                      >
                        <h2 className="textTitle animate-slide-down">
                          {content.title}
                        </h2>
                        <div className="landing-para-container">
                          <p className="textPara animate-slide-down">
                            {content.para}
                          </p>
                        </div>
                        <div
                          key={i}
                          className="get-started-button-container animate-slide-down"
                          onClick={() => handleGetStarted(content.indexName)}
                        >
                          <button className="OpenButton" type="button">
                            Get started
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="rightSection">
                      <img
                        src={imageUrl || "/placeholder.svg"}
                        alt={content.title}
                        className="contentImg-landing"
                      />
                    </div>
                  </div>
                </Slide>
              );
            })}
          </Slider>
        </CarouselProvider>
      </div>
      <TableOfContent
        onItemClick={handleItemClick}
        tableOfContent={props.tableOfContent}
        titleNames={props.titleNames}
        activeItem={activeItem}
      />
      {popup && (
        <div id="popup-container" className="popup-main-container">
          <div className="warring-popup-container small-mb-25">
            <p className="popup-text-highlight-text">Request Access</p>
            <button
              className="close-button info-button"
              type="button"
              onClick={closetoggle}
            >
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
